import { useState, useEffect } from 'react';
import { X, Download, Bell, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';

function PWAInstallBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  useEffect(() => {
    // Check if already installed as PWA
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    
    setIsInstalled(isStandalone);

    // Check if notifications are already enabled
    if ('Notification' in window && Notification.permission === 'granted') {
      // Check if subscribed to push
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready.then(registration => {
          registration.pushManager.getSubscription().then(subscription => {
            setIsSubscribed(!!subscription);
          });
        });
      }
    }

    // Check if user has dismissed the banner
    const dismissed = localStorage.getItem('pwa_banner_dismissed');
    const dismissedTime = dismissed ? parseInt(dismissed) : 0;
    const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
    
    // Show banner if not installed and not dismissed in last 7 days
    if (!isStandalone && daysSinceDismissed > 7) {
      setShowBanner(true);
    }

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for app installed event
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa_banner_dismissed', Date.now().toString());
    setShowBanner(false);
  };

  // Don't show if installed and subscribed
  if (isInstalled && isSubscribed) {
    return null;
  }

  // Don't show if user dismissed
  if (!showBanner) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-[#E63946] to-[#DC2626] rounded-xl shadow-lg p-4 mb-4 relative" data-testid="pwa-install-banner">
      {/* Dismiss button */}
      <button 
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1 rounded-full hover:bg-white/20 transition-colors"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4 text-white" />
      </button>

      <div className="flex items-start gap-3">
        <div className="bg-white/20 rounded-full p-2 flex-shrink-0">
          <Smartphone className="w-6 h-6 text-white" />
        </div>
        
        <div className="flex-1 pr-6">
          <h3 className="text-white font-bold text-sm mb-1">
            Get the Full Experience!
          </h3>
          
          <div className="space-y-2 text-white/90 text-xs">
            {!isInstalled && (
              <div className="flex items-start gap-2">
                <Download className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>Install App:</strong> Add to home screen for quick access
                </span>
              </div>
            )}
            
            {!isSubscribed && (
              <div className="flex items-start gap-2">
                <Bell className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>Enable Notifications:</strong> Tap the 🔔 bell icon to get match alerts
                </span>
              </div>
            )}
          </div>

          {/* Install button - only show if not installed and prompt available */}
          {!isInstalled && deferredPrompt && (
            <Button
              onClick={handleInstall}
              size="sm"
              className="mt-3 bg-white text-[#E63946] hover:bg-white/90 font-semibold"
              data-testid="install-pwa-btn"
            >
              <Download className="w-4 h-4 mr-1" />
              Install App
            </Button>
          )}

          {/* iOS install instructions */}
          {!isInstalled && !deferredPrompt && /iPhone|iPad|iPod/.test(navigator.userAgent) && (
            <p className="mt-2 text-white/80 text-xs">
              Tap <span className="inline-flex items-center bg-white/20 rounded px-1">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2L12 14M12 14L8 10M12 14L16 10M4 18H20V20H4V18Z"/>
                </svg>
              </span> then "Add to Home Screen"
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default PWAInstallBanner;
